import type { ReactNode } from "react"
import { Badge, type BadgeProps } from "@chakra-ui/react"

/*
 * The site's one chip: outlined, Title Case, 12px semibold, a 1px border in the chip's
 * own colour and 3px corners. Scope chips (ScopeLinkChip), alert tags (alertSummary) and
 * status tags all build on it, so they look alike wherever they appear.
 */

export interface ChipColours {
  bg: string
  color: string
  /** Defaults to the text colour, softened */
  border?: string
}

// Muted ink, as elsewhere in sections: the theme doesn't emit Chakra's semantic tokens
const MUTED = "#545454"

/** The chip style as Badge props, for chips that add their own content (e.g. a link). */
export function chipStyle({ bg, color, border }: ChipColours) {
  return {
    size: "sm",
    variant: "subtle",
    bg,
    color,
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: "7px",
    py: "1px",
    fontSize: "12px",
    fontWeight: "semibold",
    lineHeight: "1.5",
    whiteSpace: "nowrap",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: border ?? `color-mix(in srgb, ${color} 40%, transparent)`,
    borderRadius: "3px",
    flexShrink: 0,
  } satisfies BadgeProps
}

export function Chip({
  colours,
  width,
  children,
}: {
  colours: ChipColours
  /** A fixed width, for chips that should line up (e.g. status) */
  width?: string
  children: ReactNode
}) {
  return (
    <Badge {...chipStyle(colours)} minW={width} justifyContent={width ? "center" : undefined}>
      {children}
    </Badge>
  )
}

// Configuration state (ui-visual-vocabulary.md §1D), outlined like every other chip
export const STATUS_CHIP_COLOURS = {
  active: { bg: "rgba(198,246,213,.45)", color: "#22543D", border: "#68D391" },
  inactive: { bg: "#F4F4F4", color: MUTED, border: "#D0D0D0" },
} satisfies Record<string, ChipColours>

/** Whether something is switched on: one width everywhere, so a column of them lines up. */
export function StatusTag({ active }: { active: boolean }) {
  return (
    <Chip colours={active ? STATUS_CHIP_COLOURS.active : STATUS_CHIP_COLOURS.inactive} width="90px">
      {active ? "Active" : "Inactive"}
    </Chip>
  )
}

export default Chip
